import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Spin, Empty, Tag, Table, message, Button, DatePicker, Pagination } from "antd";
import {
  ArrowLeftOutlined, ReloadOutlined, BarChartOutlined, FieldTimeOutlined, DownloadOutlined,
} from "@ant-design/icons";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Customized,
} from "recharts";
import dayjs from "dayjs";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { fetchBindingAnalysis } from "../service/checklistBinding";

const PIE_COLORS = ["#1677ff", "#52c41a", "#faad14", "#f5222d", "#722ed1", "#13c2c2", "#eb2f96"];
const PASS_COLOR = "#52c41a";
const FAIL_COLOR = "#f5222d";

// ─── Parsing helpers (mirror how values are stored by ChecklistExecution) ──
const parseNumeric = (raw) => {
  if (raw === null || raw === undefined) return null;
  const v = typeof raw === "string" ? (() => { try { return JSON.parse(raw); } catch { return raw; } })() : raw;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const parseBool = (raw) => {
  if (raw === null || raw === undefined) return null;
  const v = typeof raw === "string" ? (() => { try { return JSON.parse(raw); } catch { return raw; } })() : raw;
  if (v === true || v === "true") return true;
  if (v === false || v === "false") return false;
  return null;
};

const parseText = (raw) => {
  if (raw === null || raw === undefined) return "";
  if (typeof raw === "string") { try { const p = JSON.parse(raw); return p === null || p === undefined ? "" : String(p); } catch { return raw; } }
  return String(raw);
};

// ─── Aggregation helpers ────────────────────────────────────────────────────
const buildHistogram = (values, bins = 10) => {
  if (!values.length) return [];
  const min = Math.min(...values), max = Math.max(...values);
  if (min === max) return [{ range: min.toFixed(2), count: values.length }];
  const width = (max - min) / bins;
  const buckets = Array.from({ length: bins }, (_, i) => ({
    range: `${(min + i * width).toFixed(1)}–${(min + (i + 1) * width).toFixed(1)}`,
    count: 0,
  }));
  values.forEach((v) => {
    let idx = Math.floor((v - min) / width);
    if (idx >= bins) idx = bins - 1;
    if (idx < 0) idx = 0;
    buckets[idx].count += 1;
  });
  return buckets;
};

const buildPassTrend = (bools, bucketSize = 10) => {
  const out = [];
  for (let i = 0; i < bools.length; i += bucketSize) {
    const chunk = bools.slice(i, i + bucketSize);
    const passRate = (chunk.filter(Boolean).length / chunk.length) * 100;
    out.push({ bucket: `${i + 1}–${i + chunk.length}`, passRate: Number(passRate.toFixed(1)) });
  }
  return out;
};

const buildOptionCounts = (values) => {
  const map = {};
  values.forEach((v) => { if (v) map[v] = (map[v] || 0) + 1; });
  return Object.entries(map).map(([option, count]) => ({ option, count }));
};

// Auto-thin X-axis ticks so labels never overlap when a chart IS at its
// natural (non-scrolled) width.
const tickInterval = (count, maxTicks = 12) =>
  count <= maxTicks ? 0 : Math.ceil(count / maxTicks) - 1;

// ── Same idea as antd's <Table scroll={{x}}>: give the chart's inner track
// an explicit width based on how much content it holds. If that's narrower
// than the card, minWidth:"100%" makes it fill the card exactly — NO
// scrollbar. If it's wider (lots of points/categories), the track keeps its
// natural width and the wrapper scrolls horizontally — but the scrollbar
// itself is hidden (via the CSS injected below); the user can still scroll
// with touch/trackpad/shift+wheel, it just doesn't show a visible gray bar.
const ScrollTrack = ({ itemCount, pxPerItem, height, children }) => {
  const trackWidth = Math.max(1, itemCount) * pxPerItem;
  return (
    <div className="analysis-scroll-track" style={{ overflowX: "auto", overflowY: "hidden", width: "100%", maxWidth: "100%" }}>
      <div style={{ width: trackWidth, minWidth: "100%", height, boxSizing: "border-box" }}>
        {children}
      </div>
    </div>
  );
};

// ── Chart → image conversion, purely client-side (no backend, no extra npm
// package) — recharts renders each chart as a real <svg> element with
// explicit pixel width/height, so we can serialize it, draw it onto a
// canvas, and read the canvas back out as a PNG data URL. This is what
// "Download Excel" uses to embed each chart as an image.
const svgNodeToPng = (svgEl, scale = 2) => new Promise((resolve, reject) => {
  try {
    const width = svgEl.width?.baseVal?.value || svgEl.getBoundingClientRect().width || 400;
    const height = svgEl.height?.baseVal?.value || svgEl.getBoundingClientRect().height || 260;

    // Clone so we can safely set an explicit xmlns without touching the live chart.
    const clone = svgEl.cloneNode(true);
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    clone.setAttribute("width", width);
    clone.setAttribute("height", height);

    const svgString = new XMLSerializer().serializeToString(clone);
    const svg64 = btoa(unescape(encodeURIComponent(svgString)));
    const dataUrl = `data:image/svg+xml;base64,${svg64}`;

    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = width * scale;
      canvas.height = height * scale;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff"; // charts have a transparent SVG background — force white for Excel
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve({ dataUrl: canvas.toDataURL("image/png"), width, height });
    };
    img.onerror = reject;
    img.src = dataUrl;
  } catch (e) { reject(e); }
});

// ── SVG-native legend, rendered via recharts' <Customized> ─────────────────
// recharts' built-in <Legend> renders as a plain HTML <div>/<ul> OUTSIDE the
// chart's <svg> element — it's never part of the SVG paint tree. Since our
// Excel export can only rasterize actual SVG content (svgNodeToPng above),
// that legend silently gets left out of every exported chart image. Drawing
// the legend ourselves as real <rect>/<text> SVG shapes via <Customized>
// guarantees it lives inside the same <svg> everywhere — on screen AND in
// the export — so what you see is exactly what gets downloaded.
// ── Value label drawn INSIDE each pie slice (white, bold), instead of
// recharts' default outward connector-line label. The default label sits
// outside the pie's radius on a line — with a legend now permanently drawn
// underneath the pie (see makeSvgLegend below), an outward label from a
// bottom slice can land right on top of the legend text. Keeping the label
// confined to inside the slice's own radius makes that geometrically
// impossible, regardless of how tall/short the container ends up being.
const renderInsideSliceLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, value }) => {
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.6;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  return (
    <text x={x} y={y} fill="#fff" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={700}>
      {value}
    </text>
  );
};

// chartWidth/chartHeight are passed in EXPLICITLY by the caller (measured via
// useElementWidth below, or a known fixed height) rather than read from
// <Customized>'s injected props. Those came back undefined in practice —
// which turned every position calculation into NaN, and an invalid
// `translate(NaN, NaN)` is ignored by the browser, so every legend item
// rendered at the same (0,0) origin stacked directly on top of each other.
// That's exactly what "no legend / overlapping text" was: not a rendering
// bug, but silently-broken coordinates.
const makeSvgLegend = (items, chartWidth, chartHeight) => function SvgLegend() {
  if (!items?.length) return null;
  const width = chartWidth || 300;
  const height = chartHeight || 240;
  const itemWidth = 92;
  const rowHeight = 16;
  const perRow = Math.max(1, Math.floor((width - 8) / itemWidth));
  const rows = Math.ceil(items.length / perRow);
  const startY = height - rows * rowHeight - 6;
  return (
    <g>
      {items.map((item, i) => {
        const row = Math.floor(i / perRow);
        const col = i % perRow;
        const itemsInRow = Math.min(perRow, items.length - row * perRow);
        const rowWidth = itemsInRow * itemWidth;
        const rowStartX = Math.max(4, (width - rowWidth) / 2);
        const x = rowStartX + col * itemWidth;
        const y = startY + row * rowHeight;
        const label = item.name?.length > 14 ? `${item.name.slice(0, 13)}…` : item.name;
        return (
          <g key={`${item.name}_${i}`} transform={`translate(${x}, ${y})`}>
            <rect width={10} height={10} fill={item.color} rx={2} />
            <text x={14} y={9} fontSize={11} fill="#333">{label}</text>
          </g>
        );
      })}
    </g>
  );
};

// Measures a DOM node's rendered width live (ResizeObserver) so we can feed
// a real number into makeSvgLegend instead of trusting recharts to hand us
// one. Height isn't measured — the two pie charts below use a fixed,
// known height already, no need to observe it.
const useElementWidth = () => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const update = () => setWidth(el.getBoundingClientRect().width);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
};

// ─── Field card ─────────────────────────────────────────────────────────────
// chartContainerRef: callback ref from the parent, used ONLY so "Download
// Excel" can find this field's rendered <svg> chart(s) later — has no
// effect on rendering.
const FieldCard = ({ field, chartContainerRef }) => {
  const points = field.dataPoints || [];
  // Only one of these will ever actually be used per field (a field is
  // either OK_NOT_OK or OPTIONS-as-pie, never both) — called unconditionally
  // here because hooks can't live inside the switch/case below.
  const [pieBoxRef, pieBoxWidth] = useElementWidth();

  const chartTypeLabel = useMemo(() => {
    if (field.inputType === "OK_NOT_OK") return "Pass / Fail";
    if (field.inputType === "STRING" || field.inputType === "DATE") return "Recent values";
    return field.chartType
      ? field.chartType.charAt(0) + field.chartType.slice(1).toLowerCase()
      : "—";
  }, [field]);

  const content = useMemo(() => {
    if (!points.length) {
      return <Empty description="No data yet" image={Empty.PRESENTED_IMAGE_SIMPLE} style={{ margin: "32px 0" }} />;
    }

    switch (field.inputType) {
      case "DOUBLE": {
        const series = points.map((p, i) => ({
          idx: i + 1, unit: p.serialNumber, value: parseNumeric(p.value),
        })).filter((d) => d.value !== null);

        if (!series.length) return <Empty description="No numeric data" image={Empty.PRESENTED_IMAGE_SIMPLE} />;

        if (field.chartType === "HISTOGRAM") {
          const data = buildHistogram(series.map((d) => d.value));
          return (
            <ScrollTrack itemCount={data.length} pxPerItem={70} height={260}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="range" tick={{ fontSize: 10 }}
                    interval={tickInterval(data.length)} angle={-30} textAnchor="end" height={55} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={32} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#1677ff" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ScrollTrack>
          );
        }
        // default: LINE
        return (
          <ScrollTrack itemCount={series.length} pxPerItem={16} height={260}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={series} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="idx" tick={{ fontSize: 10 }} interval={tickInterval(series.length)}
                  label={{ value: "Unit #", position: "insideBottom", offset: -2, fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} width={32} />
                <Tooltip labelFormatter={(v) => `Unit #${v}`} formatter={(v, n, p) => [v, p.payload.unit || "value"]} />
                <Line type="monotone" dataKey="value" stroke="#1677ff" dot={series.length <= 40 ? { r: 2 } : false} strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </ScrollTrack>
        );
      }

      case "OK_NOT_OK": {
        const bools = points.map((p) => parseBool(p.value)).filter((v) => v !== null);
        if (!bools.length) return <Empty description="No pass/fail data" image={Empty.PRESENTED_IMAGE_SIMPLE} />;
        const passCount = bools.filter(Boolean).length;
        const failCount = bools.length - passCount;
        const pieData = [
          { name: "OK", value: passCount },
          { name: "NOT OK", value: failCount },
        ];
        const trend = buildPassTrend(bools);
        return (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 16, width: "100%" }}>
            {/* flex: "0 1 260px" (not "1 1 ...") — this div must NOT grow to
                fill leftover row space. A pie's visible circle is capped by
                the SHORTER of width/height, so letting the container stretch
                wide just adds blank margin on both sides with nothing drawn
                there. Capped + centered keeps the chart square-ish and full. */}
            <div ref={pieBoxRef} style={{ flex: "0 1 260px", maxWidth: 260, margin: "0 auto", minWidth: 0 }}>
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={pieData} dataKey="value" nameKey="name" cy="42%" outerRadius="65%"
                    label={renderInsideSliceLabel} labelLine={false}
                  >
                    <Cell fill={PASS_COLOR} />
                    <Cell fill={FAIL_COLOR} />
                  </Pie>
                  <Customized component={makeSvgLegend([
                    { name: "OK", color: PASS_COLOR },
                    { name: "NOT OK", color: FAIL_COLOR },
                  ], pieBoxWidth || 260, 240)} />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div style={{ flex: "1 1 240px", minWidth: 0, maxWidth: "100%" }}>
              <ScrollTrack itemCount={trend.length} pxPerItem={70} height={220}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trend} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="bucket" tick={{ fontSize: 9 }} interval={tickInterval(trend.length)} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 9 }} unit="%" width={32} />
                    <Tooltip formatter={(v) => `${v}%`} />
                    <Line type="monotone" dataKey="passRate" stroke={PASS_COLOR} strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </ScrollTrack>
              <div style={{ fontSize: 11, color: "#999", textAlign: "center", marginTop: 2 }}>
                Pass-rate trend (grouped in buckets of 10 units)
              </div>
            </div>
          </div>
        );
      }

      case "OPTIONS": {
        const values = points.map((p) => parseText(p.value)).filter(Boolean);
        const data = buildOptionCounts(values);
        if (!data.length) return <Empty description="No option data" image={Empty.PRESENTED_IMAGE_SIMPLE} />;

        if (field.chartType === "PIE") {
          return (
            // Capped + centered, same reasoning as the OK/Not-OK pie above —
            // a pie's circle is capped by the shorter dimension, so a wide
            // container (this field previously spanned the FULL card width)
            // just adds blank space on both sides. Height is a bit taller
            // than the OK/Not-OK pie since Options can have more legend rows.
            <div ref={pieBoxRef} style={{ maxWidth: 320, margin: "0 auto" }}>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={data} dataKey="count" nameKey="option" cy="38%" outerRadius="55%"
                    label={renderInsideSliceLabel} labelLine={false}
                  >
                    {data.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Customized component={makeSvgLegend(
                    data.map((d, i) => ({ name: d.option, color: PIE_COLORS[i % PIE_COLORS.length] })),
                    pieBoxWidth || 320, 300
                  )} />
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          );
        }
        return (
          <ScrollTrack itemCount={data.length} pxPerItem={90} height={260}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="option" tick={{ fontSize: 10 }} interval={tickInterval(data.length)} />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={32} />
                <Tooltip />
                <Bar dataKey="count" fill="#722ed1" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ScrollTrack>
        );
      }

      case "STRING":
      case "DATE":
      default: {
        const rows = points.slice().reverse().map((p, i) => ({
          key: i,
          unit: p.serialNumber,
          po: p.poNumber,
          value: parseText(p.value),
          executedAt: p.executedAt ? dayjs(p.executedAt).format("DD/MM/YY HH:mm") : "—",
        }));
        return (
          <Table
            size="small"
            pagination={rows.length > 10 ? { pageSize: 10, size: "small" } : false}
            dataSource={rows}
            scroll={{ x: 420 }}
            style={{ fontSize: 12, width: "100%" }}
            columns={[
              { title: "Serial", dataIndex: "unit", width: 110 },
              { title: "PO", dataIndex: "po", width: 90 },
              { title: "Value", dataIndex: "value" },
              { title: "Executed", dataIndex: "executedAt", width: 120 },
            ]}
          />
        );
      }
    }
  }, [field, points, pieBoxWidth]);

  return (
    <div style={{
      border: "1px solid #eef0f2", borderRadius: 10, padding: "14px 16px",
      background: "#fff", width: "100%", maxWidth: "100%",
      boxSizing: "border-box", overflow: "hidden",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}>
      <div style={{
        display: "flex", alignItems: "flex-start", justifyContent: "space-between",
        marginBottom: 14, paddingBottom: 10, borderBottom: "1px solid #f5f5f5",
        flexWrap: "wrap", gap: 8, rowGap: 6,
      }}>
        <span style={{
          fontWeight: 600, fontSize: 14, color: "#1f1f1f",
          flex: "1 1 140px", minWidth: 0, wordBreak: "break-word",
        }}>
          {field.label}
        </span>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", flex: "0 0 auto" }}>
          <Tag color="default" style={{ fontSize: 11, margin: 0 }}>{chartTypeLabel}</Tag>
          <Tag color="blue" style={{ fontSize: 11, margin: 0 }}>{points.length} data points</Tag>
        </div>
      </div>
      <div ref={chartContainerRef} style={{ width: "100%", minWidth: 0 }}>
        {content}
      </div>
    </div>
  );
};

// How many records a single "big jump" click skips back in one go — done by
// ─── Main component ─────────────────────────────────────────────────────────
const BindingAnalysis = () => {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [limit, setLimit] = useState(100);
  // recordId -> DOM node of that field's chart container. Populated by each
  // FieldCard's chartContainerRef callback. Used only by "Download Excel" to
  // find and capture the currently-rendered <svg> chart(s) — never read
  // during normal rendering.
  const chartNodesRef = useRef({});
  // 1-based, page 1 = most recent batch (within whatever asOf view is active).
  const [page, setPage] = useState(1);
  // epoch-millis cutoff, or null = live ("now"). Persists across page/size
  // changes — picking a date doesn't just jump to a page, it changes the
  // WHOLE pagination context (totalPages/totalRecords shrink to match what
  // existed at that moment), so every subsequent request must keep sending it.
  const [asOf, setAsOf] = useState(null);

  const [messageApi, contextHolder] = message.useMessage();

  // Single entry point for every navigation: initial mount, clicking a page
  // number, changing page size, or picking/clearing a date. page and asOf
  // both apply together — asOf doesn't override page, it reshapes what
  // page/totalPages even mean.
  const load = async (nextLimit, nextPage, nextAsOf) => {
    setLoading(true);
    try {
      const res = await fetchBindingAnalysis(uuid, nextLimit, nextPage, nextAsOf ?? undefined);
      setData(res.data);
      setPage(res.data.page);
      setLimit(nextLimit);
      setAsOf(nextAsOf ?? null);
    } catch (e) {
      messageApi.error(e?.response?.data?.message || "Failed to load analysis");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(100, 1, null); /* eslint-disable-next-line */ }, [uuid]);

  // antd <Pagination> fires (page, pageSize) together, whether the user
  // clicked a page number, typed one into the quick-jumper, or changed the
  // size-changer dropdown. Keep whatever date filter is currently active.
  const handlePageChange = (nextPage, nextPageSize) => {
    load(nextPageSize, nextPage, asOf);
  };

  // Picking a date/time resets to page 1 of that NEW (possibly smaller)
  // pagination context. Clearing it goes back to the live view.
  const handlePickDateTime = (value) => {
    load(limit, 1, value ? value.valueOf() : null);
  };

  // ── Download Excel ─────────────────────────────────────────────────────
  // Exports the COMPLETE dataset (every page, not just what's on screen),
  // bounded by whatever date filter is currently active — one sheet per
  // checklist template, one block per analysis-enabled field: chart fields
  // (Number/Options/OK-NotOK) are embedded as actual chart IMAGES captured
  // straight from the screen; Text/Date fields (which were never a chart)
  // stay as a normal data table. Pure client-side — exports exactly what's
  // currently on screen (this page, this date filter), no extra API calls.
  const handleDownloadExcel = async () => {
    if (!data?.templates?.length) {
      messageApi.info("No analysis-enabled fields to export.");
      return;
    }
    setExporting(true);
    try {
      const wb = new ExcelJS.Workbook();
      wb.creator = "Checklist Binding Analysis";
      wb.created = new Date();

      const thin = { style: "thin", color: { argb: "FFD9D9D9" } };
      const border = { top: thin, bottom: thin, left: thin, right: thin };
      const usedSheetNames = new Set();
      const DEFAULT_ROW_PX = 20; // approx. default Excel row height, used to reserve enough rows under an image

      const safeSheetName = (name, sequenceNo) => {
        // Excel sheet names: max 31 chars, no \ / ? * [ ] :, and must be unique.
        let base = (name || `Template ${sequenceNo}`).replace(/[\\/?*[\]:]/g, "").slice(0, 25).trim() || `Template ${sequenceNo}`;
        let candidate = `${sequenceNo}. ${base}`.slice(0, 31);
        let n = 1;
        while (usedSheetNames.has(candidate)) {
          candidate = `${sequenceNo}. ${base}`.slice(0, 27) + `(${n++})`;
        }
        usedSheetNames.add(candidate);
        return candidate;
      };

      // Human-readable value per input type — same parsing rules the charts use.
      const displayValue = (field, raw) => {
        switch (field.inputType) {
          case "DOUBLE": {
            const n = parseNumeric(raw);
            return n === null ? "" : n;
          }
          case "OK_NOT_OK": {
            const b = parseBool(raw);
            return b === null ? "" : (b ? "OK" : "NOT OK");
          }
          default:
            return parseText(raw);
        }
      };

      // eslint-disable-next-line no-restricted-syntax
      for (const tmpl of data.templates) {
        const ws = wb.addWorksheet(safeSheetName(tmpl.checklistName, tmpl.sequenceNo));
        ws.columns = [
          { width: 20 }, { width: 16 }, { width: 16 }, { width: 22 }, { width: 20 },
        ];

        // Sheet title block
        ws.mergeCells(1, 1, 1, 5);
        const title = ws.getCell(1, 1);
        title.value = `${tmpl.sequenceNo}. ${tmpl.checklistName}`;
        title.font = { bold: true, size: 13, color: { argb: "FF1F3864" } };
        title.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFD6E4F0" } };
        title.alignment = { vertical: "middle", horizontal: "center" };
        ws.getRow(1).height = 24;

        const metaCell = ws.getCell(2, 1);
        ws.mergeCells(2, 1, 2, 5);
        metaCell.value = `${tmpl.executionsAnalyzed} executions on this page (page ${data.page} of ${data.totalPages})` +
          (data.oldestExecutedAt && data.newestExecutedAt
            ? ` · ${dayjs(data.oldestExecutedAt).format("DD/MM/YYYY HH:mm")} to ${dayjs(data.newestExecutedAt).format("DD/MM/YYYY HH:mm")}`
            : "") +
          (asOf ? ` · filtered as of ${dayjs(asOf).format("DD/MM/YYYY HH:mm")}` : "");
        metaCell.font = { italic: true, size: 10, color: { argb: "FF666666" } };
        ws.getRow(2).height = 16;

        let cursor = 4;

        // eslint-disable-next-line no-restricted-syntax
        for (const field of tmpl.fields) {
          // Field block header
          ws.mergeCells(cursor, 1, cursor, 5);
          const fieldHeader = ws.getCell(cursor, 1);
          fieldHeader.value = `${field.label}  (${field.inputType}${field.chartType ? " · " + field.chartType : ""})`;
          fieldHeader.font = { bold: true, size: 11, color: { argb: "FF7C5B00" } };
          fieldHeader.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFFFFBE6" } };
          fieldHeader.alignment = { vertical: "middle", horizontal: "left", indent: 1 };
          ws.getRow(cursor).height = 20;
          cursor += 1;

          const isChartField = field.inputType === "DOUBLE" || field.inputType === "OPTIONS" || field.inputType === "OK_NOT_OK";
          const containerNode = chartNodesRef.current[field.recordId];
          const svgs = containerNode ? Array.from(containerNode.querySelectorAll("svg")) : [];

          if (isChartField && svgs.length) {
            // eslint-disable-next-line no-restricted-syntax
            for (const svgEl of svgs) {
              // eslint-disable-next-line no-await-in-loop
              const { dataUrl, width, height } = await svgNodeToPng(svgEl);
              const base64 = dataUrl.split(",")[1];
              const imgId = wb.addImage({ base64, extension: "png" });
              ws.addImage(imgId, {
                tl: { col: 0, row: cursor - 1 },
                ext: { width, height },
              });
              cursor += Math.max(1, Math.ceil(height / DEFAULT_ROW_PX)) + 1;
            }
          } else if (isChartField) {
            // Chart type but nothing captured (e.g. field showed "No data") — note it plainly.
            ws.mergeCells(cursor, 1, cursor, 5);
            const noneCell = ws.getCell(cursor, 1);
            noneCell.value = "No chart rendered — no data for this field on the current page";
            noneCell.font = { italic: true, size: 10, color: { argb: "FFAAAAAA" } };
            cursor += 1;
          } else {
            // STRING / DATE — was always a table, not a chart, so export it as one.
            ["Serial Number", "PO Number", "Model Number", "Value", "Executed At"].forEach((h, ci) => {
              const c = ws.getCell(cursor, ci + 1);
              c.value = h;
              c.font = { bold: true, size: 10 };
              c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFF0F0F0" } };
              c.border = border;
            });
            cursor += 1;

            if (!field.dataPoints?.length) {
              ws.mergeCells(cursor, 1, cursor, 5);
              const noneCell = ws.getCell(cursor, 1);
              noneCell.value = "No data";
              noneCell.font = { italic: true, size: 10, color: { argb: "FFAAAAAA" } };
              cursor += 1;
            } else {
              field.dataPoints.forEach((p) => {
                const row = [
                  p.serialNumber || "",
                  p.poNumber || "",
                  p.modelNumber || "",
                  displayValue(field, p.value),
                  p.executedAt ? dayjs(p.executedAt).format("DD/MM/YYYY HH:mm:ss") : "",
                ];
                row.forEach((val, ci) => {
                  const c = ws.getCell(cursor, ci + 1);
                  c.value = val;
                  c.border = border;
                  c.font = { size: 10 };
                });
                cursor += 1;
              });
            }
          }

          cursor += 1; // blank spacer row between field blocks
        }
      }

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const safeBindingName = (data.bindingName || "binding").replace(/[/\\?*[\]:]/g, "_");
      saveAs(blob, `${safeBindingName}_analysis_${dayjs().format("YYYYMMDD_HHmmss")}.xlsx`);
    } catch (e) {
      console.error(e);
      messageApi.error("Failed to export Excel");
    } finally { setExporting(false); }
  };

  const isAtLatest = asOf === null;

  return (
    <Spin spinning={loading || exporting} tip={exporting ? "Building Excel file..." : undefined}>
      {contextHolder}

      {/* Hides the scrollbar chrome on chart tracks (ScrollTrack) — the
          div is still overflow-x:auto and fully scrollable via touch,
          trackpad, or shift+wheel, it just doesn't render a visible bar. */}
      <style>{`
        .analysis-scroll-track {
          scrollbar-width: none;      /* Firefox */
          -ms-overflow-style: none;   /* old IE/Edge */
        }
        .analysis-scroll-track::-webkit-scrollbar {
          height: 0px;
          width: 0px;
          background: transparent;    /* Chrome/Safari/Edge (Chromium) */
        }
      `}</style>

      <div style={{ width: "100%", maxWidth: "100%", boxSizing: "border-box", overflowX: "hidden" }}>
        {/* Header bar */}
        <div style={{
          display: "flex", alignItems: "center", gap: 12, marginBottom: 12,
          flexWrap: "wrap", padding: "14px 18px", background: "#fff",
          border: "1px solid #f0f0f0", borderRadius: 10, boxSizing: "border-box",
        }}>
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate("/check-list-builder/binding")} />
          <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
            <BarChartOutlined style={{ fontSize: 18, color: "#1677ff", flex: "0 0 auto" }} />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: "1.05rem", lineHeight: 1.2, wordBreak: "break-word" }}>
                {data ? data.bindingName : "Analysis"}
              </div>
              {data && (
                <div style={{ fontSize: 12, color: "#888", wordBreak: "break-word" }}>{data.productName}</div>
              )}
            </div>
          </div>
          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <Button
              size="small" icon={<DownloadOutlined />}
              onClick={handleDownloadExcel} loading={exporting} disabled={!data}
              title="Exports charts on this page as images into an Excel file"
            >
              Download Excel
            </Button>
            <Button size="small" icon={<ReloadOutlined />} onClick={() => load(limit, page, asOf)} />
          </div>
        </div>

        {/* Jump-to-date/time toolbar */}
        <div style={{
          display: "flex", alignItems: "center", gap: 10, marginBottom: 18,
          flexWrap: "wrap", padding: "10px 18px", background: "#fff",
          border: "1px solid #f0f0f0", borderRadius: 10, boxSizing: "border-box",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <FieldTimeOutlined style={{ color: "#888" }} />
            <DatePicker
              size="small"
              showTime
              placeholder="Pick date & time, then click OK"
              value={asOf ? dayjs(asOf) : null}
              // NOTE: with showTime enabled, antd's DatePicker only commits
              // the final value when the popup's OK button is clicked — just
              // clicking a calendar cell does NOT fire onChange (that's what
              // made this look like "nothing happens"). onOk is the
              // guaranteed-fire event for a confirmed pick; onChange here is
              // only needed to catch the "clear" (✕) action, which reports
              // value === null.
              onOk={handlePickDateTime}
              onChange={(value) => { if (!value) handlePickDateTime(null); }}
              allowClear
            />
          </div>

          <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "#888" }}>
            {data ? (
              isAtLatest ? (
                <span>Showing most recent data — {data.totalRecords} total records</span>
              ) : (
                <>
                  <Tag color="purple" style={{ margin: 0, fontSize: 11 }}>
                    As of {dayjs(asOf).format("DD/MM/YYYY HH:mm")} · {data.totalRecords} records existed then ({data.totalPages} pages)
                  </Tag>
                  <Button size="small" type="link" style={{ padding: 0 }} onClick={() => handlePickDateTime(null)}>
                    Clear filter
                  </Button>
                </>
              )
            ) : null}
          </div>
        </div>

        {/* Real numbered pagination — like the standard antd Table pagination,
            backed by data.totalRecords. When a date filter is active,
            totalRecords/totalPages reflect what existed AS OF that moment,
            not the live total. showQuickJumper lets you type a page number
            directly (e.g. "10" of 16) instead of clicking through. */}
        {data && (
          <div style={{
            display: "flex", justifyContent: "flex-end", marginBottom: 18,
            padding: "10px 18px", background: "#fff",
            border: "1px solid #f0f0f0", borderRadius: 10, boxSizing: "border-box",
          }}>
            <Pagination
              current={page}
              total={data.totalRecords}
              pageSize={limit}
              showSizeChanger
              showQuickJumper
              pageSizeOptions={["25", "50", "100", "200"]}
              showTotal={(total) => `${total} total records (max across templates)`}
              onChange={handlePageChange}
              onShowSizeChange={handlePageChange}
            />
          </div>
        )}

        {!data || !data.templates?.length ? (
          <Empty
            style={{ marginTop: 60 }}
            description="No analysis-enabled fields found. Add an 'Analysis Label' to at least one input field in the checklist templates used by this binding."
          />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 26, width: "100%" }}>
            {data.templates.map((tmpl) => (
              <div key={tmpl.checklistId} style={{ width: "100%", minWidth: 0 }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 10, marginBottom: 12,
                  padding: "9px 14px", background: "#f0f5ff", borderRadius: 8,
                  border: "1px solid #d6e4ff", flexWrap: "wrap", boxSizing: "border-box",
                }}>
                  <Tag color="blue" style={{ fontWeight: 700, margin: 0 }}>#{tmpl.sequenceNo}</Tag>
                  <span style={{ fontWeight: 600, fontSize: 13.5, wordBreak: "break-word" }}>{tmpl.checklistName}</span>
                  <Tag style={{ marginLeft: "auto" }}>{tmpl.executionsAnalyzed} executions analyzed</Tag>
                </div>

                {/* Full-width, stacked cards */}
                <div style={{ display: "flex", flexDirection: "column", gap: 14, width: "100%" }}>
                  {tmpl.fields.map((field) => (
                    <FieldCard
                      key={field.recordId}
                      field={field}
                      chartContainerRef={(el) => { if (el) chartNodesRef.current[field.recordId] = el; }}
                    />
                  ))}
                </div>
              </div>
            ))}

            {data.totalPages > 1 && (
              <div style={{ display: "flex", justifyContent: "flex-end", padding: "4px 4px 20px" }}>
                <Pagination
                  current={page}
                  total={data.totalRecords}
                  pageSize={limit}
                  showSizeChanger
                  showQuickJumper
                  pageSizeOptions={["25", "50", "100", "200"]}
                  onChange={handlePageChange}
                  onShowSizeChange={handlePageChange}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </Spin>
  );
};

export default BindingAnalysis;